// Epic Pushback Types and Event Definitions
// Defines standardized events that trigger Epic write-back

export type EpicPushbackEventType = 
  | 'case_created'
  | 'submitted'
  | 'more_info'
  | 'approved'
  | 'denied'
  | 'appealed'
  | 'closed';

export type EpicPushbackDestination = 'inbasket' | 'task' | 'chart_note';

export type EpicPushbackStatus = 'PENDING' | 'SENT' | 'FAILED' | 'RETRYING';

export type EpicEnvironment = 'sandbox' | 'production';

export interface EpicIntegrationSettings {
  id: string;
  client_id: string;
  client_name: string;
  environment: EpicEnvironment;
  epic_enabled: boolean;
  
  // OAuth/SMART settings
  fhir_base_url?: string;
  oauth_client_id?: string;
  oauth_redirect_uri?: string;
  
  // Pushback configuration
  pushback_enabled: boolean;
  pushback_inbasket: boolean;
  pushback_task: boolean;
  pushback_chart_note: boolean;
  default_pushback_destination: EpicPushbackDestination;
  
  // Event toggles
  pushback_on_case_created: boolean;
  pushback_on_submitted: boolean;
  pushback_on_more_info: boolean;
  pushback_on_approved: boolean;
  pushback_on_denied: boolean;
  pushback_on_appealed: boolean;
  pushback_on_closed: boolean;
  
  created_at: string;
  updated_at: string;
}

export interface EpicPushbackTemplate {
  id: string;
  client_id: string;
  event_type: EpicPushbackEventType;
  template_name: string;
  subject_template: string;
  body_template: string;
  is_active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface EpicPushbackLog {
  id: string;
  authorization_id: string;
  client_id: string;
  event_type: EpicPushbackEventType;
  destination: EpicPushbackDestination;
  status: EpicPushbackStatus;
  
  // Request/Response
  request_payload?: Record<string, unknown>;
  response_payload?: Record<string, unknown>;
  error_message?: string;
  
  // FHIR references
  fhir_resource_type?: string;
  fhir_resource_id?: string;
  
  // Retry tracking
  attempt_count: number;
  last_attempt_at: string;
  next_retry_at?: string;
  
  // Metadata
  triggered_by?: string;
  is_manual_resend: boolean;
  created_at: string;
}

// Event configuration labels
export const EVENT_TYPE_LABELS: Record<EpicPushbackEventType, string> = {
  case_created: 'Case Created',
  submitted: 'Submitted to Payer',
  more_info: 'More Info Requested',
  approved: 'Approved',
  denied: 'Denied',
  appealed: 'Appeal Filed',
  closed: 'Case Closed',
};

export const DESTINATION_LABELS: Record<EpicPushbackDestination, string> = {
  inbasket: 'Epic InBasket',
  task: 'Epic Task',
  chart_note: 'Chart Note',
};

export const STATUS_LABELS: Record<EpicPushbackStatus, string> = {
  PENDING: 'Pending',
  SENT: 'Sent',
  FAILED: 'Failed',
  RETRYING: 'Retrying',
};

export const STATUS_COLORS: Record<EpicPushbackStatus, string> = {
  PENDING: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  SENT: 'bg-primary/10 text-primary border-primary/30',
  FAILED: 'bg-red-500/10 text-red-500 border-red-500/30',
  RETRYING: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
};

// Template placeholder definitions
export const TEMPLATE_PLACEHOLDERS = {
  patient_initials: 'Patient initials (e.g., JD)',
  mrn: 'Medical Record Number',
  case_number: 'Authorization case number',
  payer_name: 'Payer name',
  plan_name: 'Plan name',
  cpt_codes: 'CPT code(s)',
  modifiers: 'Modifier(s)',
  laterality: 'Laterality (RT/LT/BL)',
  site_of_service: 'Site of service',
  normalized_status: 'Current status',
  payer_reference: 'Payer reference number',
  auth_number: 'Authorization number',
  effective_start: 'Auth effective start date',
  effective_end: 'Auth effective end date',
  denial_category: 'Denial category',
  denial_reason: 'Denial reason',
  next_action: 'Recommended next action',
  more_info_details: 'Information requested by payer',
} as const;

export type TemplatePlaceholder = keyof typeof TEMPLATE_PLACEHOLDERS;

// Map authorization status to pushback event type
export function mapStatusToEventType(status: string): EpicPushbackEventType | null {
  const statusEventMap: Record<string, EpicPushbackEventType> = {
    pending_surgeon_approval: 'case_created',
    submitted_to_payer: 'submitted',
    more_info_requested: 'more_info',
    payer_approved: 'approved',
    payer_denied: 'denied',
    appeal_in_progress: 'appealed',
    closed: 'closed',
    resolved: 'closed',
  };
  
  return statusEventMap[status] || null;
}
